// src/pages/MileageHistory.js
import React, { useState, useContext, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import TabContext from "@/contexts/TabContext";
import {
    useListPageFilter,
    comma,
    uncomma,
    dateFormatDateTime,
    dateFormatToISOString,
    ReservationStatusType,
    formatNumberWithCommas,
    onlyNumericInput,
} from "@/utils";
import { PageTabs, Pagination, Toast, Confirm } from "@/components";

import { useGetMyInfoQuery } from "@/redux/memberApi";
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import {
    useChargeMileageMutation,
    useWithdrawMileageMutation,
    useGetMileageStatusQuery,
    useGetUsableMileageQuery,
    useGetMileageHistoryQuery,
    useGetPointHistoryQuery,
} from "@/redux/mileageApi";
import { useGetBankListQuery } from "@/redux/metaApi";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import Select, { components } from "react-select";

// 페이지 기본 필터
const pageFilter = {
    parameters: {
        page: 0,
        size: 20,
        query: "",
        status: "",
        started_at: "",
        ended_at: "",
    },
};

export const MileageHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state: tabs, dispatch } = useContext(TabContext);

    // 탭(토글) 상태: 1 / 2
    const [currentTab, setCurrentTab] = useState(1);

    const { formData, handleChange, setFormData, parameters, toastMsg } = useListPageFilter(pageFilter, {
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        orderDetailPopupStatus: false,
        mileageChargePopupStatus: false,
        mileageWithdrawalPopupStatus: false,
        mileage: 0,
        name: "",
        mileageWithdraw: 0,
        triggerSearch: false,
    });

    const { confirmStatus, orderDetailPopupStatus, mileageChargePopupStatus, mileageWithdrawalPopupStatus, triggerSearch } = formData;

    // 탭 추가 로직
    useEffect(() => {
        const handleTabAdd = () => {
            const existingTab = tabs.find((tab) => tab.url.split("?")[0] === location.pathname);
            if (!existingTab) {
                dispatch({
                    type: "ADD_TAB",
                    payload: { url: location.pathname + location.search, title: "마일리지" },
                });
            }
        };
        handleTabAdd();
    }, [dispatch, location.pathname, location.search, tabs]);

    const { data: myInfoResponse, isLoading: isMyInfoLoading, isError: isMyInfoError, error: myInfoError } = useGetMyInfoQuery();

    // currentTab 값에 따라 API 호출
    const { data: mileageData, error: errorMileage } = useGetMileageHistoryQuery(parameters, {
        skip: currentTab !== 1 || !triggerSearch, // currentTab이 1가 아니면 스킵
        refetchOnMountOrArgChange: true,
    });

    const { data: pointData, error: errorPoint } = useGetPointHistoryQuery(parameters, {
        skip: currentTab !== 2 || !triggerSearch, // currentTab이 2이 아니면 스킵
        refetchOnMountOrArgChange: true,
    });

    // // 아래처럼 필요한 데이터를 선택해서 사용
    const data = currentTab === 1 ? mileageData : pointData;
    const error = currentTab === 1 ? errorMileage : errorPoint;

    // 컴포넌트 마운트 시 초기 데이터 조회
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            triggerSearch: true,
        }));
    }, []);

    // 검색 버튼 클릭
    const handleSearch = () => {
        setFormData((prev) => ({
            ...prev,
            parameters: { ...prev.parameters, page: 0 },
            triggerSearch: true,
        }));
    };

    const memberData = myInfoResponse?.data;

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management container">
                <PageTabs />

                <div className="table-container">
                    <div className="table-head">
                        <span className="head-tit">마일리지 관리</span>
                        <div className="breadcrumbs">
                            <ul>
                                <li className="home"></li>
                                <li>마일리지</li>
                                <li>마일리지 관리</li>
                            </ul>
                        </div>
                    </div>
                    <TableFilter formData={formData} handleChange={handleChange} setFormData={setFormData} handleSearch={handleSearch} />

                    <div className="table-content">
                        <div className="table-option">
                            {/* <div className="toggle-list">
                                <button
                                    type="button"
                                    className={`btn-toggle ${currentTab === 1 ? "active" : ""}`}
                                    onClick={() => setCurrentTab(1)}
                                >
                                    마일리지
                                </button>
                            </div> */}
                            <span className="data-count">
                                전체 <b>{data?.pagination?.totalElements}</b>건
                            </span>
                            <div className="table-buttons">
                                <button
                                    type="button"
                                    className="btn-table_option btn-complete"
                                    onClick={() => setFormData((prev) => ({ ...prev, mileageChargePopupStatus: true }))}
                                >
                                    마일리지 충전
                                </button>
                                <button
                                    type="button"
                                    className="btn-table_option btn-outline"
                                    onClick={() => setFormData((prev) => ({ ...prev, mileageWithdrawalPopupStatus: true }))}
                                >
                                    마일리지 출금
                                </button>
                            </div>
                        </div>

                        <div className="table-wrapper">
                            <div className="table">
                                {currentTab === 1 && (
                                    <table>
                                        <colgroup>
                                            <col width="10%" />
                                            <col width="30%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">날짜/시간</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">사용내역</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">충전/적립</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">마일리지 차감</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">잔여 마일리지</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">계약번호</span>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data && data.data && data.data.length > 0 ? (
                                                data.data.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {dateFormatDateTime(item.createdAt, "YYYY-MM-DD HH:mm:ss")}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.reason}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {item.usedType === "PLUS" ? comma(item.actionAmount) : 0}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {item.usedType === "MINUS" ? comma(item.actionAmount) : 0}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-tag">{comma(item.afterAmount)}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-tag">-</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="data-empty">
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">데이터가 없습니다.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {data?.data?.length ? <Pagination pagination={data?.pagination} setFormData={setFormData} /> : null}
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus?.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {mileageChargePopupStatus && (
                <MileageChargePopup
                    popupStatus={mileageChargePopupStatus}
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, mileageChargePopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    memberData={memberData}
                />
            )}

            {mileageWithdrawalPopupStatus && (
                <MileageWithdrawalPopup
                    popupStatus={mileageWithdrawalPopupStatus}
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, mileageWithdrawalPopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    memberData={memberData}
                />
            )}
        </>
    );
};

const TableFilter = ({ formData, setFormData, handleSearch }) => {
    const [filterOption, setFilterOption] = useState([]);
    const [clientLabel, setClientLabel] = useState({});
    const [statusLabel, setStatusLabel] = useState({});

    const { parameters } = formData;
    const [activeButton, setActiveButton] = useState(null);

    // const { data, error, isLoading } = useGetClientNameListQuery();

    // useEffect(() => {
    //     if (data?.data) {
    //         var res = [{ value: null, label: "전체" }];
    //         data?.data?.forEach((i) => {
    //             res.push({ value: i.id, label: i.clientCorName });
    //         });
    //         setFilterOption(res);
    //     }
    // }, [data]);

    const handleParameterChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                [name]: value,
            },
            triggerSearch: false,
        }));
    };

    const handleDateChange = (dates, type) => {
        const [start, end] = dates;

        if (type === 1) {
            setFormData((prev) => ({
                ...prev,
                parameters: {
                    ...prev.parameters,
                    started_at: start ? dateFormatToISOString(start, "start") : "",
                    ended_at: end ? dateFormatToISOString(end, "end") : "",
                },
                triggerSearch: false,
            }));
        }

        if (type === "service") {
            setFormData((prev) => ({
                ...prev,
                parameters: {
                    ...prev.parameters,
                    reserveDateStart: start ? dateFormatToISOString(start, "start") : "",
                    reserveDateEnd: end ? dateFormatToISOString(end, "end") : "",
                },
                triggerSearch: false,
            }));
        }

        setActiveButton(null);
    };

    const setDateRange = (days, buttonIndex) => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(start.getDate() - days);

        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                page: 0,
                started_at: dateFormatToISOString(start, "start"),
                ended_at: dateFormatToISOString(now, "end"),
            },
            triggerSearch: false,
        }));

        setActiveButton(buttonIndex);
    };

    return (
        <div className="table-filter">
            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">상태 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-select">
                            <Select
                                options={ReservationStatusType}
                                className="filter-select"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                                placeholder="진행현황"
                                value={statusLabel}
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: {
                                            ...prev.parameters,
                                            status: option.value,
                                        },
                                    }));
                                    setStatusLabel(option);
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">등록일 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-input">
                            <DatePicker
                                locale={ko}
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={20}
                                dateFormat="yyyy.MM.dd"
                                onKeyDown={(e) => e.preventDefault()}
                                selectsRange={true}
                                startDate={parameters.started_at ? new Date(parameters.started_at) : null}
                                endDate={parameters.ended_at ? new Date(parameters.ended_at) : null}
                                onChange={(e) => handleDateChange(e, 1)}
                            />
                        </div>
                    </div>
                </div>
                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-buttons">
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 0 ? "active" : ""}`}
                                onClick={() => setDateRange(0, 0)}
                            >
                                당일
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 15 ? "active" : ""}`}
                                onClick={() => setDateRange(15, 15)}
                            >
                                15일
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 30 ? "active" : ""}`}
                                onClick={() => setDateRange(30, 30)}
                            >
                                1개월
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 90 ? "active" : ""}`}
                                onClick={() => setDateRange(90, 90)}
                            >
                                3개월
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-input" style={{ width: "300px" }}>
                            <input
                                type="text"
                                placeholder="입력하세요."
                                name="query"
                                value={parameters.query}
                                onChange={handleParameterChange}
                            />
                        </div>
                        <div className="item-buttons">
                            <button type="button" className="btn-search" onClick={handleSearch}>
                                검색
                            </button>
                            <button
                                type="button"
                                className="btn-search"
                                onClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        ...pageFilter,
                                    }));
                                    setClientLabel({ value: null, label: "전체" });
                                }}
                            >
                                초기화
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 마일리지 충전 안내 팝업
const MileageChargePopup = ({ popupStatus, layer = true, onClose, formData, handleChange, setFormData, memberData }) => {
    const [chargeMileage, { isLoading: isCharging }] = useChargeMileageMutation();
    const [mileage, setMileage] = useState(null);

    // 사업자 리스트 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    useEffect(() => {
        setFormData((prev) => ({ ...prev, bankOwner: memberData.member.name }));
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    const handleChargeConfirm = async () => {
        if (!formData.bankOwner) {
            setFormData((prev) => ({ ...prev, toastMsg: "입금자명을 입력해주세요." }));
            return;
        }
        if (!formData.rentCorpId) {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자를 선택해주세요." }));
            return;
        }

        try {
            const requestBody = {
                mileage: Number(mileage),
                name: formData?.bankOwner,
                rentCorpId: formData?.rentCorpId,
            };
            await chargeMileage(requestBody).unwrap();
            setFormData((prev) => ({ ...prev, toastMsg: "마일리지 충전 요청이 완료되었습니다." }));
            onClose();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "마일리지 충전 요청에 실패하였습니다.",
            }));
        }
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup mileage-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>마일리지 충전</span>
                    </div>
                    <div className="content-description">
                        <p>입금 시 30분 내로 마일리지가 충전 됩니다.</p>
                    </div>
                    <div className="content-textbox">
                        <div className="item-list">
                            <div className="item">
                                <span className="item-tit">계좌정보</span>
                                <span className="item-desc">하나은행 542-910035-60004</span>
                            </div>
                            <div className="item">
                                <span className="item-tit">예금주</span>
                                <span className="item-desc">주식회사 모빌리카</span>
                            </div>
                        </div>
                    </div>
                    <div className="content-description">
                        <p>
                            <b>마일리지 및 포인트 정책</b>
                            <br />- 마일리지는 환불이 가능하며, 포인트는 환불이 불가 합니다.
                        </p>
                        <p>
                            <b>차감 순서</b>
                            <br />
                            마일리지 차감 이후 포인트가 차감 됩니다.
                        </p>
                        <p>
                            <b>마일리지 환불 안내</b>
                            <br />
                            환불 요청 시 고객센터 이메일로 접수 부탁드립니다. <br />
                            이메일 주소 : info@mobilicar.kr
                        </p>
                    </div>
                    <div className="content-mileage-info">
                        <div className="table-container">
                            <div className="table-content" style={{ padding: "0" }}>
                                <div className="table-wrapper">
                                    <div className="table">
                                        <table>
                                            <colgroup>
                                                <col width={50} />
                                                <col width={50} />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="cell">
                                                            <span className="cell-tit">입금액(VAT 포함)</span>
                                                        </div>
                                                    </th>
                                                    <th>
                                                        <div className="cell">
                                                            <span className="cell-tit">충전금액</span>
                                                        </div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <div className="cell">
                                                            <div className="radiobox">
                                                                <input
                                                                    type="radio"
                                                                    name="mileage"
                                                                    id="mileage_300000"
                                                                    checked={mileage === 300000}
                                                                    onChange={(e) => setMileage(300000)}
                                                                />
                                                                <label htmlFor="mileage_300000">330,000원</label>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell">
                                                            <span className="cell-desc">300,000마일리지</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <div className="cell">
                                                            <div className="radiobox">
                                                                <input
                                                                    type="radio"
                                                                    name="mileage"
                                                                    id="mileage_500000"
                                                                    checked={mileage === 500000}
                                                                    onChange={(e) => setMileage(500000)}
                                                                />
                                                                <label htmlFor="mileage_500000">550,000원</label>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell">
                                                            <span className="cell-desc">500,000마일리지</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <div className="cell">
                                                            <div className="radiobox">
                                                                <input
                                                                    type="radio"
                                                                    name="mileage"
                                                                    id="mileage_1000000"
                                                                    checked={mileage === 1000000}
                                                                    onChange={(e) => setMileage(1000000)}
                                                                />
                                                                <label htmlFor="mileage_1000000">1,100,000원</label>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell">
                                                            <span className="cell-desc">1,000,000마일리지</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="table-filter">
                                <div className="filter-row">
                                    <div className="filter-item">
                                        <div className="item-head">
                                            <span className="head-tit">입금자명</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-input">
                                                <input
                                                    type="text"
                                                    placeholder="사업자를 선택해주세요."
                                                    name="name"
                                                    value={formData?.bankOwner || ""}
                                                    onChange={handleChange}
                                                    style={{ textAlign: "left" }}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="filter-item">
                                        <div className="item-head">
                                            <span className="head-tit">사업자</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-input">
                                                <Select
                                                    options={corpOptions}
                                                    className="custom-select"
                                                    classNamePrefix="custom-select"
                                                    isSearchable={false}
                                                    placeholder="사업자를 선택해주세요."
                                                    value={corpOptions.find((opt) => opt.value === formData?.rentCorpId)}
                                                    onChange={(option) => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            rentCorpId: option.value,
                                                            bankOwner: option.label,
                                                        }));
                                                    }}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="content-description" style={{ marginTop: "12px" }}>
                        <p>선택하신 사업자로 계산서가 발행 되며, 입력하신 입금자명으로 입금해 주셔야 빠른 충전이 가능합니다.</p>
                    </div>
                </div>
                <div className="popup-bottom">
                    <div className="popup-bottom">
                        <button className="btn btn-primary btn-cancel" onClick={onClose}>
                            취소
                        </button>
                        <button className="btn btn-primary btn-close" onClick={handleChargeConfirm}>
                            확인
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

// 마일리지 출금 요청 팝업
const MileageWithdrawalPopup = ({ popupStatus, layer = true, onClose, formData, handleChange, setFormData, memberData }) => {
    const [withdrawMileage, { isLoading: isWithdrawing }] = useWithdrawMileageMutation();
    const { data: bankData, isLoading: bankLoading } = useGetBankListQuery();

    // 은행 Select 예시
    const bankOptions = bankData?.data
        ? bankData.data.map((bank) => ({
              value: bank.code,
              label: bank.name,
              icon: bank.imgUrl,
          }))
        : [];

    // 커스텀 옵션: 드롭다운에서는 이미지와 라벨 모두 표시
    const IconOption = (props) => (
        <components.Option {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} />
            {props.data.label}
        </components.Option>
    );

    // 커스텀 SingleValue: 선택된 값은 라벨만 표시
    const CustomSingleValue = (props) => (
        <components.SingleValue {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} /> {props.data.label}
        </components.SingleValue>
    );

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    const handleWithdrawConfirm = async () => {
        const availableMileage = Number(memberData?.member?.mileage) || 0;
        const withdrawAmount = Number(uncomma(formData?.mileageWithdraw)) || 0;

        // 출금 요청 금액이 사용 가능한 마일리지보다 많을 경우 에러 메시지 표시
        if (withdrawAmount > availableMileage) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "출금 요청 금액이 보유 마일리지를 초과하였습니다.",
            }));
            return;
        }

        if (withdrawAmount < 1000) {
            setFormData((prev) => ({ ...prev, toastMsg: "1,000 마일리지부터 출금이 가능합니다." }));
            return;
        }

        if (!formData.bankName) {
            setFormData((prev) => ({ ...prev, toastMsg: "은행을 선택해주세요." }));
            return;
        }

        if (!formData.bankAccount) {
            setFormData((prev) => ({ ...prev, toastMsg: "계좌번호를 입력해주세요." }));
            return;
        }

        if (!formData.bankOwner) {
            setFormData((prev) => ({ ...prev, toastMsg: "예금주를 입력해주세요." }));
            return;
        }

        try {
            const requestBody = {
                mileage: Number(uncomma(formData.mileageWithdraw)),
                bankName: formData.bankName,
                bankAccount: formData.bankAccount,
                bankOwner: formData.bankOwner,
            };
            await withdrawMileage(requestBody).unwrap();
            setFormData((prev) => ({ ...prev, toastMsg: "마일리지 출금 요청이 완료되었습니다." }));
            onClose();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "마일리지 출금 요청에 실패하였습니다.",
            }));
        }
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup mileage-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>마일리지 출금</span>
                    </div>
                    <div className="content-description">
                        <p>출금 요청 안내 문구입니다.</p>
                    </div>
                    <div className="content-textbox">
                        <div className="item-list">
                            <div className="mileage">
                                <span className="item-tit">출금 가능 마일리지</span>
                                <span className="item-desc">
                                    <em>{memberData?.member?.mileage || "0"}</em> M
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="content-input">
                        <div className="table-container">
                            <div className="table-filter">
                                <div className="filter-row">
                                    <div className="filter-item">
                                        <div className="item-head">
                                            <span className="head-tit">출금 마일리지</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-input">
                                                <input
                                                    type="text"
                                                    placeholder="출금할 마일리지를 입력하세요."
                                                    name="mileageWithdraw"
                                                    value={formData?.mileageWithdraw || ""}
                                                    onChange={handleChange}
                                                    onInput={formatNumberWithCommas}
                                                    style={{ textAlign: "left" }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="filter-item">
                                        <div className="item-head">
                                            <span className="head-tit">은행명</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-input">
                                                {/* <input
                                                    type="text"
                                                    placeholder="은행을 선택하세요."
                                                    name="mileageWithdraw"
                                                    value={formData?.mileageWithdraw || ""}
                                                    onChange={handleChange}
                                                    onInput={formatNumberWithCommas}
                                                    style={{ textAlign: "right" }}
                                                /> */}

                                                <Select
                                                    options={bankOptions}
                                                    className="custom-select"
                                                    classNamePrefix="custom-select"
                                                    placeholder="은행을 선택해주세요."
                                                    value={bankOptions.find((opt) => opt.value === formData?.bankName)}
                                                    onChange={(option) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            bankName: option.value,
                                                        }))
                                                    }
                                                    components={{ Option: IconOption, SingleValue: CustomSingleValue }}
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                        singleValue: (provided) => ({
                                                            ...provided,
                                                            textAlign: "right",
                                                        }),
                                                        option: (provided) => ({
                                                            ...provided,
                                                            textAlign: "right",
                                                        }),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="filter-item">
                                        <div className="item-head">
                                            <span className="head-tit">계좌번호</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-input">
                                                <input
                                                    type="text"
                                                    placeholder="계좌번호를 입력하세요."
                                                    name="bankAccount"
                                                    value={formData?.bankAccount || ""}
                                                    onChange={handleChange}
                                                    onInput={onlyNumericInput}
                                                    style={{ textAlign: "left" }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="filter-item">
                                        <div className="item-head">
                                            <span className="head-tit">예금주</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-input">
                                                <input
                                                    type="text"
                                                    placeholder="예금주를 입력하세요."
                                                    name="bankOwner"
                                                    value={formData?.bankOwner || ""}
                                                    onChange={handleChange}
                                                    style={{ textAlign: "left" }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <div className="popup-bottom">
                        <button className="btn btn-primary btn-cancel" onClick={onClose}>
                            취소
                        </button>
                        <button className="btn btn-primary btn-close" onClick={handleWithdrawConfirm}>
                            출금요청
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};
